/**
 * French messages of the session feature (start, open, join, guide,
 * building, close, wrap-up). Merged into the catalogue by `src/i18n/fr.ts`
 * under the `session` key, read everywhere via `t('session.…')`.
 */
export const sessionMessages = {
  nav: {
    start: 'Démarrer',
    cases: 'Relances',
    summary: 'Bilan',
    account: 'Compte',
    ariaLabel: 'Navigation principale',
  },
  card: {
    kindStandard: 'PAP standard',
    kindRegistration: 'Spécial inscription',
    binomes: '{{count}} binômes',
    binome: '1 binôme',
    distance: '{{distance}} m',
    by: 'par {{name}}',
    doorsProgress: '{{recorded}} / {{total}} portes',
  },
  start: {
    title: 'Que fait-on aujourd’hui ?',
    startButton: 'Démarrer une session de porte-à-porte',
    startHint:
      'Vous posez le point de départ sur la carte. Les autres militant·es rejoignent votre session et avancent avec vous : le même immeuble, puis la même rue, puis la suivante.',
    openSessionsKicker: 'Sessions ouvertes · à rejoindre',
    openSessionsEmpty: 'Aucune session ouverte pour le moment.',
    seeAllSessions: 'Voir les sessions en cours',
    myCasesKicker: 'Vos relances',
    myCasesCta: 'Les mails partent tout seuls, vous voyez qui répond.',
    loadError: 'Impossible de charger les sessions ouvertes.',
  },
  open: {
    title: 'Où commence-t-on ?',
    lead: 'Touchez la carte pour poser le point de départ. La localisation est facultative : on peut préparer une session depuis chez soi, la veille.',
    mapLabel: 'Carte : toucher pour poser le point de départ',
    useMyPosition: 'Utiliser ma position (facultatif)',
    pointPlaceholder: 'Point posé : {{longitude}}, {{latitude}}',
    addressLabel: 'Adresse de départ (facultatif)',
    addressPlaceholder: 'Ex. : 35 rue Léon Jouhaux',
    kindKicker: 'Type de session',
    kindStandard: 'PAP standard',
    kindRegistration: 'Spécial inscription',
    kindHint:
      'Le PAP inscription ne montre que les portes sans inscrit, ou dont tout le foyer n’a pas voté (peut-être mal inscrit).',
    nameLabel: 'Nom de la session',
    namePlaceholder: 'Ex. : Orée du golf · tours B et C',
    visibilityKicker: 'Qui peut rejoindre',
    visibilityGroup: 'Tout le groupe d’action',
    visibilityInvite: 'Sur invitation',
    visibilityHint:
      'La session distribue les portes entre les binômes présents : deux personnes ne sonnent jamais à la même.',
    submit: 'Ouvrir la session',
    cancel: 'Annuler',
    noCommune: 'Aucune commune rattachée à votre compte.',
    mapUnavailable: 'Carte indisponible : posez le point plus tard depuis un autre appareil.',
    missingPoint: 'Posez un point de départ sur la carte.',
    missingName: 'Donnez un nom à la session.',
  },
  join: {
    title: 'Sessions en cours',
    lead: 'Vous rejoignez là où la session en est. Elle vous envoie la porte suivante libre — pendant que les autres avancent sur les leurs.',
    empty: 'Aucune session ouverte à rejoindre pour le moment.',
    joinCta: 'Rejoindre cette session',
    joining: 'Connexion…',
    back: '← Retour',
    openedBy: 'Ouverte à {{time}} par {{name}}',
  },
  guide: {
    metaTitle: 'Session',
    doorHeroLoading: 'Recherche de la prochaine porte…',
    arrivedButton: 'J’y suis',
    skipButton: 'Passer cette porte → une autre',
    skipButtonOffline: 'Passer cette porte dès le retour du réseau',
    stopButton: 'Je m’arrête · la session continue',
    closeButton: 'Clore la session pour tout le monde',
    binomeInfo: 'Porte attribuée par la session',
    buildingBadge: 'Immeuble · {{count}} portes',
    nonVotersBadge: '{{count}} foyers non-votants',
    toVerifyBadge: '{{count}} à vérifier',
    canvassTargetBadge: 'Foyer non-votant',
    registrationTargetBadge: 'Probablement non inscrit',
    noAssignmentTitle: 'Aucune porte en cours',
    noAssignmentBody: 'La session va vous attribuer la porte libre la plus proche.',
    getNextDoor: 'Recevoir une porte',
    noDoorLeftTitle: 'Plus aucune porte',
    noDoorLeftBody:
      'Toutes les portes de cette session ont été attribuées ou renseignées. Vous pouvez clore la session pour tout le monde, ou vous arrêter et laisser la session ouverte pour les autres binômes.',
    directionLabel: '↗ {{direction}}',
    noDistance: 'Distance inconnue',
  },
  building: {
    metaTitle: 'Bâtiment',
    lead: 'Mode bâtiment : GPS en pause, la session répartit les étages entre les binômes présents.',
    floorKicker: 'Étage {{floor}}',
    floorUnknown: 'Étage inconnu',
    finishFloor: 'Étage fini → porte suivante de la session',
    status: {
      done: 'Fait',
      target: 'Cible',
      held: 'Prise · {{name}}',
      toVerify: 'À vérifier',
      toVerifyPhantom: 'À vérifier · confiance {{confidence}}',
    },
    electors: '{{count}} inscrits',
    noElectors: 'Aucun inscrit connu',
    nonVoters: '{{count}} non-votants',
  },
  /** `docs/PHANTOM.md` §5: what the volunteer sees on a phantom door. */
  phantom: {
    badge: 'Non inscrit ? · confiance {{confidence}}',
    badgeUnknown: 'Non inscrit ?',
    confidence: {
      high: 'forte',
      medium: 'moyenne',
      low: 'faible',
    },
  },
  close: {
    title: 'Clore « {{name}} » ?',
    lead: 'Les {{count}} binômes présents seront prévenus et recevront le bilan.',
    pendingWarningTitle: '{{count}} portes attribuées ne sont pas renseignées.',
    pendingWarningBody:
      'En clôturant, elles sont désattribuées et repassent en « non vue » : elles ressortiront dans la prochaine session du secteur. Rien n’est perdu, rien n’est compté comme visité.',
    participantsKicker: 'Qui participe',
    confirm: 'Clore et voir le bilan',
    continue: 'Continuer le porte-à-porte',
  },
  summary: {
    title: 'Belle session.',
    lead: '{{recorded}} portes renseignées à {{participants}} binômes.',
    recapKicker: 'Récapitulatif de la session',
    tileRecorded: 'Portes renseignées',
    tileParticipants: 'Binômes mobilisés',
    tileQuality: 'Qualité moyenne des échanges',
    tileReleased: 'Portes non vues, remises au stock',
    releasedKicker: 'Ce qui repart dans le stock',
    releasedReminder:
      'Une porte non vue n’est pas une porte visitée : elle ne compte ni dans la couverture, ni dans les statistiques de participation.',
    noQuality: '—',
    finish: 'Terminer',
    encouragementDefault: 'Merci pour cette session : chaque porte renseignée compte.',
  },
  errors: {
    no_door_left: 'Plus aucune porte à visiter dans cette session.',
    session_closed: 'Cette session est close.',
    not_a_member: 'Vous n’êtes pas membre de ce groupe d’action.',
    not_allowed: 'Action non autorisée.',
    generic: 'Une erreur est survenue, réessayez dans un instant.',
  },
  common: {
    loading: 'Chargement…',
    retry: 'Réessayer',
  },
} as const;
